import { useEffect, useState } from "react";
import { format, parseISO } from "date-fns";
import { CalendarIcon, ScanLine } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { PART_CATEGORIES, money } from "@/lib/shop";
import { ScannerDialog } from "./ScannerDialog";

const blank = { name: "", category: "Screens", brand: "", compatible_model: "", supplier: "", purchase_cost: "", selling_price: "", quantity: "0", min_stock: "1", location: "", barcode: "", purchase_date: "", notes: "" };
const NUMS = ["purchase_cost", "selling_price"];
const INTS = ["quantity", "min_stock"];

export const PartForm = ({ part, onClose, onSave }) => {
  const [f, setF] = useState(blank);
  const [scan, setScan] = useState(false);
  const editing = !!part?.id;

  useEffect(() => {
    if (part) setF({ ...blank, ...Object.fromEntries(Object.entries(part).map(([k, v]) => [k, v ?? ""])) });
  }, [part]);

  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = (e) => {
    e.preventDefault();
    const out = {};
    Object.keys(blank).forEach((k) => {
      const v = typeof f[k] === "string" ? f[k].trim() : f[k];
      if (NUMS.includes(k)) out[k] = v === "" ? null : Number(v);
      else if (INTS.includes(k)) out[k] = v === "" ? 0 : parseInt(v, 10);
      else out[k] = v === "" ? null : v;
    });
    if (editing) delete out.quantity;
    onSave(out);
  };

  const profit = f.purchase_cost !== "" && f.selling_price !== "" ? Number(f.selling_price) - Number(f.purchase_cost) : null;
  const date = f.purchase_date ? parseISO(f.purchase_date) : undefined;

  return (
    <Dialog open={!!part} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="part-form-dialog">
        <DialogHeader><DialogTitle>{editing ? "Edit part" : "Add part"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="grid gap-3">
          <div className="grid sm:grid-cols-2 gap-3">
            <Row label="Part name *"><Input required value={f.name} onChange={set("name")} data-testid="part-name" /></Row>
            <Row label="Category *">
              <Input required list="part-categories" value={f.category} onChange={set("category")} data-testid="part-category" />
              <datalist id="part-categories">{PART_CATEGORIES.map((c) => <option key={c} value={c} />)}</datalist>
            </Row>
            <Row label="Brand"><Input value={f.brand} onChange={set("brand")} data-testid="part-brand" /></Row>
            <Row label="Compatible phone model(s)"><Input placeholder="e.g. iPhone 13, iPhone 13 Pro" value={f.compatible_model} onChange={set("compatible_model")} data-testid="part-model" /></Row>
            <Row label="Supplier"><Input value={f.supplier} onChange={set("supplier")} data-testid="part-supplier" /></Row>
            <Row label="Storage location / bin"><Input placeholder="e.g. Drawer A3" value={f.location} onChange={set("location")} data-testid="part-location" /></Row>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 rounded-xl bg-slate-50 p-3">
            <Row label="Purchase cost (£)"><Input type="number" step="0.01" min="0" value={f.purchase_cost} onChange={set("purchase_cost")} data-testid="part-cost" /></Row>
            <Row label="Selling / repair price (£)"><Input type="number" step="0.01" min="0" value={f.selling_price} onChange={set("selling_price")} data-testid="part-price" /></Row>
            <Row label={editing ? "Quantity in stock" : "Opening quantity"}>
              <Input type="number" min="0" step="1" value={f.quantity} onChange={set("quantity")} disabled={editing} data-testid="part-quantity" />
            </Row>
            <Row label="Minimum stock level"><Input type="number" min="0" step="1" value={f.min_stock} onChange={set("min_stock")} data-testid="part-min-stock" /></Row>
            <p className="col-span-2 sm:col-span-4 text-xs text-slate-500" data-testid="part-profit-preview">
              Profit per part: <b className={profit != null && profit < 0 ? "text-red-600" : "text-ink"}>{money(profit)}</b>
              {editing && " · Change stock with the Add / Remove stock buttons so it's recorded in the history."}
            </p>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <Row label="Barcode / QR code">
              <div className="flex gap-2">
                <Input value={f.barcode} onChange={set("barcode")} data-testid="part-barcode" />
                <button type="button" onClick={() => setScan(true)} className="shrink-0 inline-flex items-center gap-1 rounded-md border px-3 text-sm font-semibold hover:border-brand hover:text-brand" data-testid="part-barcode-scan-btn"><ScanLine className="h-4 w-4" /> Scan</button>
              </div>
            </Row>
            <Row label="Purchase date">
              <Popover>
                <PopoverTrigger asChild>
                  <button type="button" className="flex h-9 w-full items-center gap-2 rounded-md border border-input px-3 text-sm text-left" data-testid="part-date-trigger">
                    <CalendarIcon className="h-4 w-4 text-slate-500" />
                    {date ? format(date, "d MMM yyyy") : <span className="text-slate-500">Pick a date</span>}
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar mode="single" selected={date} onSelect={(d) => setF({ ...f, purchase_date: d ? format(d, "yyyy-MM-dd") : "" })} initialFocus />
                </PopoverContent>
              </Popover>
            </Row>
          </div>
          <Row label="Notes"><Textarea rows={2} value={f.notes} onChange={set("notes")} data-testid="part-notes" /></Row>
          <button className="mt-1 rounded-full bg-brand py-3 font-bold text-white hover:bg-brand-700" data-testid="part-save-btn">Save part</button>
        </form>
        <ScannerDialog open={scan} onOpenChange={setScan} onCode={(code) => setF((cur) => ({ ...cur, barcode: code }))} />
      </DialogContent>
    </Dialog>
  );
};

const Row = ({ label, children }) => <div className="grid gap-1"><Label className="text-xs font-semibold">{label}</Label>{children}</div>;
